/** SUNTRONIX ONE — Approvals Inbox chip.
 *
 *  Compact ONE Bar chip that polls /api/approvals every 45s.  Shows a
 *  badge with pending count and a popover with the top 6 items, each
 *  with inline Approve / Reject buttons.  A "View all" link jumps to
 *  the full inbox page.
 *
 *  Only rendered when the current user's role is likely to see approvals
 *  (super_admin, suntronix_ops_admin, customer_finance,
 *  customer_global_admin, operations_admin) — every other role gets no
 *  chip so the ONE Bar stays uncrowded.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Check, X, ArrowUpRight } from "lucide-react";

export const APPROVER_ROLES = new Set([
  "super_admin", "suntronix_ops_admin", "operations_admin",
  "customer_finance", "customer_global_admin", "customer_operations_admin",
]);

const POLL_MS = 45_000;

function fmtAmount(a, ccy) {
  if (a === null || a === undefined) return null;
  try {
    return new Intl.NumberFormat("en-US", {
      style: ccy ? "currency" : "decimal",
      currency: ccy || undefined,
      maximumFractionDigits: 0,
    }).format(a);
  } catch { return String(a); }
}

export default function ApprovalsInboxChip() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState(null);   // id currently being decided
  const navigate = useNavigate();

  const canSee = !!user && APPROVER_ROLES.has(user.role);

  const load = useCallback(async () => {
    if (!canSee) return;
    setLoading(true);
    try {
      const { data } = await api.get("/approvals",
        { params: { status: "pending", limit: 20 } });
      // Only show items for the approver's role (or all, for super_admin)
      const filtered = (data.items || []).filter((it) =>
        user.role === "super_admin" || user.role === "suntronix_ops_admin"
          ? true
          : it.approver_role === user.role);
      setItems(filtered);
    } catch { /* ignore transient errors */ }
    finally { setLoading(false); }
  }, [canSee, user]);

  useEffect(() => {
    if (!canSee) return;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [canSee, load]);

  const decide = async (id, decision) => {
    setDeciding(id);
    try {
      await api.post(`/approvals/${id}/decide`, { decision });
      toast.success(decision === "approve" ? "Approved" : "Rejected");
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${decision}`);
    } finally { setDeciding(null); }
  };

  const total = items.length;

  if (!canSee) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="relative h-9 w-9 grid place-items-center rounded-md text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]"
          data-testid="one-bar-approvals"
          aria-label={total ? `${total} pending approvals` : "Approvals inbox"}
          title={total ? `${total} pending approvals` : "Approvals inbox"}
        >
          <ClipboardCheck className="h-4 w-4" strokeWidth={1.6} />
          {total > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 grid place-items-center rounded-full bg-[color:var(--st-accent)] text-white text-[10px] font-semibold leading-none"
              data-testid="approvals-badge">
              {total > 99 ? "99+" : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[380px] p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
            Pending approvals
          </div>
          {total > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)] font-semibold">
              {total}
            </span>
          )}
          <button
            onClick={() => navigate("/approvals-inbox")}
            className="ml-auto text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5"
            data-testid="approvals-view-all">
            View all <ArrowUpRight className="h-3 w-3" />
          </button>
        </div>

        {loading && items.length === 0 && (
          <div className="text-[12px] text-slate-400 text-center py-6">
            Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="text-[12px] text-slate-500 text-center py-6"
            data-testid="approvals-empty">
            You’re all caught up.
          </div>
        )}

        <div className="space-y-2 max-h-[360px] overflow-y-auto">
          {items.slice(0, 6).map((it) => (
            <div key={it.id} className="border border-slate-200 rounded-md p-2 text-[12px]"
              data-testid={`approval-item-${it.id}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 capitalize">
                    {(it.action_type || "").replace(/_/g, " ")}
                    {it.amount != null && (
                      <span className="text-slate-500 font-normal ml-1">
                        · {fmtAmount(it.amount)}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {it.reason || it.reference || "—"}
                  </div>
                  {it.requested_by && (
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      by {it.requested_by}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <Button size="sm" className="h-7 px-2"
                    disabled={deciding === it.id}
                    onClick={() => decide(it.id, "approve")}
                    data-testid={`approve-${it.id}`}>
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2"
                    disabled={deciding === it.id}
                    onClick={() => decide(it.id, "reject")}
                    data-testid={`reject-${it.id}`}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 6 && (
          <div className="pt-2 border-t border-slate-100 mt-2">
            <Link to="/approvals-inbox"
              className="text-[11px] text-slate-500 hover:text-slate-800 block text-center"
              data-testid="approvals-see-more">
              +{items.length - 6} more · View all
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
